/**
 * hiprint 字段注册模块
 * 基于源文件 billPrint.f3475924.js 的配置实现
 * 
 * 使用方式：
 * import { registerAllBillTypes, registerBillType, getProvidersArr } from '@/services/hiprint-register'
 */

// hiprint 模块引用
let hiprintModule = null

/**
 * 获取 hiprint 模块
 * @returns {Object} hiprint 模块实例
 */
export function getHiprint() {
  if (hiprintModule) return hiprintModule
  if (window.hiprint) return window.hiprint
  if (window.Vue && window.Vue.prototype && window.Vue.prototype.$hiprint) {
    hiprintModule = window.Vue.prototype.$hiprint
    return hiprintModule
  }
  return null
}

/**
 * 初始化 hiprint 模块
 * @param {Object} hiprint - hiprint 模块实例
 */
export function initHiprint(hiprint) {
  hiprintModule = hiprint
}

// ========== 共享常量 ==========

/**
 * 公司信息（bt）- 所有单据类型共享
 */
const COMPANY_INFO = [
  {
    tid: "companyName",
    title: "公司名称",
    type: "text",
    options: { field: "companyName", testData: "@公司名称", fontSize: 10.5, lineHeight: 13.5 }
  },
  {
    tid: "companyContacts",
    title: "联系人",
    type: "text",
    options: { field: "companyContacts", testData: "@联系人", fontSize: 10.5, lineHeight: 13.5 }
  },
  {
    tid: "companyAddress",
    title: "公司地址",
    type: "text",
    options: { field: "companyAddress", testData: "@公司地址", fontSize: 10.5, lineHeight: 13.5 }
  },
  {
    tid: "companyTel",
    title: "公司电话",
    type: "text",
    options: { field: "companyTel", testData: "@公司电话", fontSize: 10.5, lineHeight: 13.5 }
  },
  {
    tid: "companyFax",
    title: "公司传真",
    type: "text",
    options: { field: "companyFax", testData: "@公司传真", fontSize: 10.5, lineHeight: 13.5 }
  },
  {
    tid: "companySaleAgreement",
    title: "销售协议",
    type: "text",
    options: { field: "companySaleAgreement", testData: "@销售协议", fontSize: 10.5, lineHeight: 13.5 }
  }
]

/**
 * 其他信息（ut）- 所有单据类型共享
 */
const OTHER_INFO = [
  { tid: "barcode", title: "条形码", type: "barcode" },
  { tid: "qrcode", title: "二维码", type: "qrcode" },
  { tid: "logo", title: "Logo", data: "", type: "image" },
  {
    tid: "customText",
    title: "文本",
    customText: "自定义文本",
    custom: true,
    type: "text"
  },
  {
    tid: "longText",
    title: "长文本",
    type: "longText",
    options: {
      field: "test.longText",
      width: 200,
      testData: "长文本分页"
    }
  },
  { tid: "hline", title: "横线", type: "hline" },
  { tid: "vline", title: "竖线", type: "vline" },
  { tid: "rect", title: "矩形", type: "rect" },
  { tid: "oval", title: "椭圆", type: "oval" }
]

// ========== 动态扩展字段标题 ==========

let extendTitles = {
  extend1: "扩展1",
  extend2: "扩展2",
  extend3: "扩展3"
}

/**
 * 更新扩展字段标题（从 localStorage 获取）
 */
export function updateExtendTitles() {
  try {
    const materialPropertyList = localStorage.getItem('materialPropertyList')
    if (materialPropertyList) {
      const list = JSON.parse(materialPropertyList)
      if (list && Array.isArray(list) && list.length >= 3) {
        extendTitles.extend1 = list[0] || "扩展1"
        extendTitles.extend2 = list[1] || "扩展2"
        extendTitles.extend3 = list[2] || "扩展3"
      }
    }
  } catch (e) {
    // 使用默认值
  }
  return extendTitles
}

/**
 * 获取扩展字段标题
 */
export function getExtendTitles() {
  return extendTitles
}

// ========== 辅助函数 ==========

/**
 * 创建文本元素
 */
function textElement(tid, title, field, testData, hideTitle = false) {
  const options = { field, testData, fontSize: 10.5, lineHeight: 13.5 }
  if (hideTitle) {
    options.hideTitle = true
    options.width = 350
    options.height = 15
    options.fontSize = 15
  }
  return { tid, title, type: "text", options }
}

/**
 * 创建表格元素
 */
function createTableElement(billType, columns) {
  return {
    tid: "table_" + billType,
    title: "表格信息",
    type: "table",
    options: { field: "table", fontSize: 10.5, lineHeight: 13.5 },
    columns: [columns]  // 二维数组
  }
}

// ========== 各单据类型的配置 ==========

/**
 * 单据类型配置
 * 所有字段名和配置基于源文件 billPrint.f3475924.js
 */
const BILL_CONFIGS = {
  // ========== 零售管理 ==========

  /**
   * 零售出库（LSCK）
   */
  LSCK: {
    name: "零售出库单",
    type: "零售出库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "会员卡号", "organName", "@会员卡号"),
      textElement("contacts", "联系人", "contacts", "@联系人"),
      textElement("telephone", "手机号码", "telephone", "@手机号码"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("payType", "收款类型", "payType", "@收款类型"),
      textElement("changeAmount", "单据金额", "changeAmount", "@单据金额"),
      textElement("getAmount", "收款金额", "getAmount", "@收款金额"),
      textElement("backAmount", "找零", "backAmount", "@找零"),
      textElement("accountName", "收款账户", "accountName", "@收款账户"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  /**
   * 零售退货（LSTH）
   */
  LSTH: {
    name: "零售退货单",
    type: "零售退货入库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "会员卡号", "organName", "@会员卡号"),
      textElement("contacts", "联系人", "contacts", "@联系人"),
      textElement("telephone", "手机号码", "telephone", "@手机号码"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联单据", "linkNumber", "@关联单据"),
      textElement("changeAmount", "单据金额", "changeAmount", "@单据金额"),
      textElement("getAmount", "付款金额", "getAmount", "@付款金额"),
      textElement("backAmount", "找零", "backAmount", "@找零"),
      textElement("accountName", "付款账户", "accountName", "@付款账户"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  // ========== 采购管理 ==========

  /**
   * 请购单（QGD）
   */
  QGD: {
    name: "请购单",
    type: "请购单",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "供应商", "organName", "@供应商"),
      textElement("contacts", "供应商姓名", "contacts", "@供应商姓名"),
      textElement("telephone", "供应商手机", "telephone", "@供应商手机"),
      textElement("address", "供应商地址", "address", "@供应商地址"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "单位", field: "unit" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "已采购", field: "finishNumber" }
    ]
  },

  /**
   * 采购订单（CGDD）
   */
  CGDD: {
    name: "采购订单",
    type: "采购订单",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "供应商", "organName", "@供应商"),
      textElement("contacts", "供应商姓名", "contacts", "@供应商姓名"),
      textElement("telephone", "供应商手机", "telephone", "@供应商手机"),
      textElement("address", "供应商地址", "address", "@供应商地址"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("discount", "优惠率", "discount", "@优惠率"),
      textElement("discountMoney", "付款优惠", "discountMoney", "@付款优惠"),
      textElement("discountLastMoney", "优惠后金额", "discountLastMoney", "@优惠后金额"),
      textElement("accountName", "结算账户", "accountName", "@结算账户"),
      textElement("changeAmount", "支付订金", "changeAmount", "@支付订金"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "已采购", field: "finishNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "税率(%)", field: "taxRate" },
      { title: "税额", field: "taxMoney" },
      { title: "价税合计", field: "taxLastMoney" }
    ]
  },

  /**
   * 采购入库（CGRK）
   */
  CGRK: {
    name: "采购入库",
    type: "采购入库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "供应商", "organName", "@供应商"),
      textElement("contacts", "供应商姓名", "contacts", "@供应商姓名"),
      textElement("telephone", "供应商手机", "telephone", "@供应商手机"),
      textElement("address", "供应商地址", "address", "@供应商地址"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联订单", "linkNumber", "@关联订单"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "已入库", field: "finishNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "税率(%)", field: "taxRate" },
      { title: "税额", field: "taxMoney" },
      { title: "价税合计", field: "taxLastMoney" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  /**
   * 采购退货（CGTH）
   */
  CGTH: {
    name: "采购退货",
    type: "采购退货出库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "供应商", "organName", "@供应商"),
      textElement("contacts", "供应商姓名", "contacts", "@供应商姓名"),
      textElement("telephone", "供应商手机", "telephone", "@供应商手机"),
      textElement("address", "供应商地址", "address", "@供应商地址"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联单据", "linkNumber", "@关联单据"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "已出库", field: "finishNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "税率(%)", field: "taxRate" },
      { title: "税额", field: "taxMoney" },
      { title: "价税合计", field: "taxLastMoney" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  // ========== 销售管理 ==========

  /**
   * 销售订单（XSDD）
   */
  XSDD: {
    name: "销售订单",
    type: "销售订单",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "客户", "organName", "@客户"),
      textElement("contacts", "客户联系人", "contacts", "@客户联系人"),
      textElement("telephone", "客户手机", "telephone", "@客户手机"),
      textElement("address", "客户地址", "address", "@客户地址"),
      textElement("bankName", "客户开户行", "bankName", "@客户开户行"),
      textElement("accountNumber", "客户账号", "accountNumber", "@客户账号"),
      textElement("taxNum", "客户税号", "taxNum", "@客户税号"),
      textElement("phoneNum", "客户电话", "phoneNum", "@客户电话"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联订单", "linkNumber", "@关联订单"),
      textElement("discount", "优惠率", "discount", "@优惠率"),
      textElement("discountMoney", "收款优惠", "discountMoney", "@收款优惠"),
      textElement("discountLastMoney", "优惠后金额", "discountLastMoney", "@优惠后金额"),
      textElement("otherMoney", "其它费用", "otherMoney", "@其它费用"),
      textElement("accountName", "结算账户", "accountName", "@结算账户"),
      textElement("deposit", "扣除订金", "deposit", "@扣除订金"),
      textElement("changeAmount", "本次收款", "changeAmount", "@本次收款"),
      textElement("debt", "本次欠款", "debt", "@本次欠款"),
      textElement("allNeed", "累计欠款", "allNeed", "@累计欠款"),
      textElement("salesManStr", "销售人员", "salesManStr", "@销售人员"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "已采购", field: "finishPurchaseNumber" },
      { title: "已销售", field: "finishNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "税率(%)", field: "taxRate" },
      { title: "税额", field: "taxMoney" },
      { title: "价税合计", field: "taxLastMoney" }
    ]
  },

  /**
   * 销售出库（XSCK）
   */
  XSCK: {
    name: "销售出库",
    type: "销售出库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "客户", "organName", "@客户"),
      textElement("contacts", "客户联系人", "contacts", "@客户联系人"),
      textElement("telephone", "客户手机", "telephone", "@客户手机"),
      textElement("address", "客户地址", "address", "@客户地址"),
      textElement("bankName", "客户开户行", "bankName", "@客户开户行"),
      textElement("accountNumber", "客户账号", "accountNumber", "@客户账号"),
      textElement("taxNum", "客户税号", "taxNum", "@客户税号"),
      textElement("phoneNum", "客户电话", "phoneNum", "@客户电话"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联单据", "linkNumber", "@关联单据"),
      textElement("discount", "优惠率", "discount", "@优惠率"),
      textElement("discountMoney", "收款优惠", "discountMoney", "@收款优惠"),
      textElement("discountLastMoney", "优惠后金额", "discountLastMoney", "@优惠后金额"),
      textElement("otherMoney", "其它费用", "otherMoney", "@其它费用"),
      textElement("accountName", "结算账户", "accountName", "@结算账户"),
      textElement("deposit", "扣除订金", "deposit", "@扣除订金"),
      textElement("changeAmount", "本次收款", "changeAmount", "@本次收款"),
      textElement("debt", "本次欠款", "debt", "@本次欠款"),
      textElement("allNeed", "累计欠款", "allNeed", "@累计欠款"),
      textElement("salesManStr", "销售人员", "salesManStr", "@销售人员"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "已出库", field: "finishNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "税率(%)", field: "taxRate" },
      { title: "税额", field: "taxMoney" },
      { title: "价税合计", field: "taxLastMoney" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  /**
   * 销售退货（XSTH）
   */
  XSTH: {
    name: "销售退货",
    type: "销售退货入库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("organName", "客户", "organName", "@客户"),
      textElement("contacts", "客户联系人", "contacts", "@客户联系人"),
      textElement("telephone", "客户手机", "telephone", "@客户手机"),
      textElement("address", "客户地址", "address", "@客户地址"),
      textElement("bankName", "客户开户行", "bankName", "@客户开户行"),
      textElement("accountNumber", "客户账号", "accountNumber", "@客户账号"),
      textElement("taxNum", "客户税号", "taxNum", "@客户税号"),
      textElement("phoneNum", "客户电话", "phoneNum", "@客户电话"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联单据", "linkNumber", "@关联单据"),
      textElement("discount", "优惠率", "discount", "@优惠率"),
      textElement("discountMoney", "收款优惠", "discountMoney", "@收款优惠"),
      textElement("discountLastMoney", "优惠后金额", "discountLastMoney", "@优惠后金额"),
      textElement("otherMoney", "其它费用", "otherMoney", "@其它费用"),
      textElement("accountName", "结算账户", "accountName", "@结算账户"),
      textElement("deposit", "扣除订金", "deposit", "@扣除订金"),
      textElement("changeAmount", "本次收款", "changeAmount", "@本次收款"),
      textElement("debt", "本次欠款", "debt", "@本次欠款"),
      textElement("allNeed", "累计欠款", "allNeed", "@累计欠款"),
      textElement("salesManStr", "销售人员", "salesManStr", "@销售人员"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "已入库", field: "finishNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "税率(%)", field: "taxRate" },
      { title: "税额", field: "taxMoney" },
      { title: "价税合计", field: "taxLastMoney" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  // ========== 仓库管理 ==========

  /**
   * 其他入库（QTRK）
   */
  QTRK: {
    name: "其他入库",
    type: "其他入库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  /**
   * 其他出库（QTCK）
   */
  QTCK: {
    name: "其他出库",
    type: "其他出库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "序列号", field: "snList", width: 300 },
      { title: "批号", field: "batchNumber" },
      { title: "有效期", field: "expirationDate" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  /**
   * 调拨出库（DBCK）
   */
  DBCK: {
    name: "调拨出库",
    type: "调拨出库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "调入仓库", field: "anotherDepotName" },
      { title: "单位", field: "unit" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "重量", field: "weight" },
      { title: "仓位货架", field: "position" }
    ]
  },

  /**
   * 组装单（ZZD）
   */
  ZZD: {
    name: "组装单",
    type: "组装单",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "商品类型", field: "mType" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" }
    ]
  },

  /**
   * 拆卸单（CXD）
   */
  CXD: {
    name: "拆卸单",
    type: "拆卸单",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "商品类型", field: "mType" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "多属性", field: "sku" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" }
    ]
  },

  /**
   * 销售排行（SALES_RANKING）
   * 报表类型 — 单据主体含查询条件，单据明细含销售明细行
   */
  SALES_RANKING: {
    name: "销售排行",
    type: "销售排行",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@销售排行", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("operatorName", "操作员", "operatorName", "@操作员"),
      textElement("customerName", "客户", "customerName", "@客户"),
      textElement("depotName", "仓库", "depotName", "@仓库"),
      textElement("categoryName", "类别", "categoryName", "@类别"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("sortBy", "排序方式", "sortBy", "@排序方式"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "商品名称", field: "materialName" },
      { title: "商品规格", field: "materialStandard" },
      { title: "商品类型", field: "categoryName" },
      { title: "销售数量", field: "totalQty" },
      { title: "销售金额", field: "totalAmount" }
    ]
  },
  ITEM_DETAIL: {
    name: "收支项明细",
    type: "收支项明细",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@收支项明细", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("billNo", "单据编号", "billNo", "@单据编号"),
      textElement("organName", "往来单位", "organName", "@往来单位"),
      textElement("billCategory", "单据类别", "billCategory", "@单据类别"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalIncome", "收入合计", "totalIncome", "@收入合计"),
      textElement("totalExpense", "支出合计", "totalExpense", "@支出合计")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "单据编号", field: "billNo" },
      { title: "往来单位", field: "organName" },
      { title: "财务人员", field: "handsPersonName" },
      { title: "操作员", field: "userName" },
      { title: "账户信息", field: "accountName" },
      { title: "单据类别", field: "type" },
      { title: "收支项目", field: "inOutItemName" },
      { title: "收入", field: "itemInAmount" },
      { title: "支出", field: "itemOutAmount" },
      { title: "单据日期", field: "billTimeStr" },
      { title: "备注", field: "remark" }
    ]
  },
  SALE_RATIO: {
    name: "销售占比",
    type: "销售占比",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@销售占比", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("categoryName", "类别", "categoryName", "@类别"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalAllNum", "销售数量合计", "totalAllNum", "@销售数量合计"),
      textElement("totalAllPrice", "销售金额合计", "totalAllPrice", "@销售金额合计")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "商品名称", field: "mName" },
      { title: "类别名称", field: "categoryName" },
      { title: "销售数量", field: "allNum" },
      { title: "销售金额", field: "allPrice" },
      { title: "销售占比", field: "ratio" }
    ]
  },
  SALE_PROFIT: {
    name: "利润报表",
    type: "利润报表",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@利润报表", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      // 新增：三个合计字段
      textElement("total_salePrice", "合计-销售金额", "total_salePrice", "@合计-销售金额"),
      textElement("total_purchasePrice", "合计-销售成本", "total_purchasePrice", "@合计-销售成本"),
      textElement("total_profitPrice", "合计-销售利润", "total_profitPrice", "@合计-销售利润"),
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "单据编号", field: "number" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "mName" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "单位", field: "mUnit" },
      { title: "数量", field: "operNumber" },
      { title: "销售单价", field: "unitPrice" },
      { title: "成本价", field: "purchaseUnitPrice" },
      { title: "销售金额", field: "salePrice" },
      { title: "成本金额", field: "purchasePrice" },
      { title: "利润", field: "profitPrice" },
      { title: "利润率", field: "profitRate" },
      { title: "税率", field: "taxRate" },
      { title: "客户或会员", field: "sName" }
    ]
  },
  PROFIT_REPORT: {
    name: "利润报表",
    type: "利润报表",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@利润报表", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("profit", "利润", "profit", "@利润"),
      textElement("incomeTotal", "收入合计", "incomeTotal", "@收入合计"),
      textElement("paymentTotal", "支出合计", "paymentTotal", "@支出合计"),
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "项目", field: "item" },
      { title: "本期金额", field: "amount" }
    ]
  },
  MINUS_STOCK: {
    name: "负库存统计",
    type: "负库存统计",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@负库存统计", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("total_currentNumber", "合计-当前库存", "total_currentNumber", "@合计-当前库存")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "单位", field: "unit" },
      { title: "单价", field: "purchaseDecimal" },
      { title: "当前库存", field: "currentNumber" }
    ]
  },
  MONEY_DETAIL: {
    name: "收付款明细",
    type: "收付款明细",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@收付款明细", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalMoneyIn", "收款合计", "totalMoneyIn", "@收款合计"),
      textElement("totalMoneyOut", "付款合计", "totalMoneyOut", "@付款合计")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "单据编号", field: "billNo" },
      { title: "往来单位", field: "organName" },
      { title: "财务人员", field: "handsPersonName" },
      { title: "操作员", field: "userName" },
      { title: "账户信息", field: "accountName" },
      { title: "单据类别", field: "type" },
      { title: "采购或销售单号", field: "billNumber" },
      { title: "原欠款", field: "needDebt" },
      { title: "收款", field: "moneyInAmount" },
      { title: "付款", field: "moneyOutAmount" },
      { title: "单据日期", field: "billTimeStr" },
      { title: "备注", field: "remark" }
    ]
  },
  /**
 * 盘点录入（PDLR）
 */
  PDLR: {
    name: "盘点录入",
    type: "盘点录入",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "盘点数量", field: "operNumber" },
      { title: "成本价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "备注", field: "remark" }
    ]
  },

  /**
   * 盘点复盘（PDFP）
   */
  PDFP: {
    name: "盘点复盘",
    type: "盘点复盘",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联单据", "linkNumber", "@关联单据"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "颜色", field: "color" },
      { title: "品牌", field: "brand" },
      { title: "制造商", field: "mfrs" },
      { title: "扩展1", field: "otherField1" },
      { title: "扩展2", field: "otherField2" },
      { title: "扩展3", field: "otherField3" },
      { title: "库存", field: "stock" },
      { title: "单位", field: "unit" },
      { title: "盘点数量", field: "checkNumber" },
      { title: "差异", field: "diffNumber" },
      { title: "成本价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "备注", field: "remark" }
    ]
  },

  /**
   * 资产申领（NSLD）
   */
  NSLD: {
    name: "资产申领",
    type: "资产申领",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "单位", field: "unit" },
      { title: "数量", field: "operNumber" },
      { title: "备注", field: "remark" }
    ]
  },

  /**
   * 资产出库（NCKD）
   */
  NCKD: {
    name: "资产出库",
    type: "资产出库",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@单据抬头", true),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("linkNumber", "关联单据", "linkNumber", "@关联单据"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "仓库名称", field: "depotName" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "单位", field: "unit" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" },
      { title: "备注", field: "remark" }
    ]
  },

ATTRIBUTE_STOCK: {
    name: "多属性库存统计",
    type: "多属性库存统计",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@多属性库存统计", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "类别", field: "categoryName" },
      { title: "单位", field: "unit" },
      { title: "多属性", field: "sku" },
      { title: "扩展信息", field: "expand" },
      { title: "成本价", field: "purchaseDecimal" },
      { title: "多属性库存", field: "skuStock" },
      { title: "库存金额", field: "skuStockPrice" }
    ]
  },

SERIAL_NUMBER_STATISTICS: {
    name: "序列号统计",
    type: "序列号统计",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@序列号统计", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("serialNumber", "序列号", "serialNumber", "@序列号"),
      textElement("materialParam", "商品", "materialParam", "@商品"),
      textElement("depotName", "仓库", "depotName", "@仓库"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("total_inPrice", "合计-入库单价", "total_inPrice", "@合计-入库单价")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "序列号", field: "serialNumber" },
      { title: "条码", field: "materialCode" },
      { title: "名称", field: "materialName" },
      { title: "规格", field: "materialStandard" },
      { title: "型号", field: "materialModel" },
      { title: "仓库", field: "depotName" },
      { title: "入库单价", field: "inPrice" },
      { title: "入库单号", field: "inBillNo" },
      { title: "出库单号", field: "outBillNo" },
      { title: "已出库", field: "isSell" },
      { title: "创建时间", field: "createTimeStr" },
      { title: "更新时间", field: "updateTimeStr" }
    ]
  },

  // ========== 固定资产单据 ==========

  /**
   * 固定资产卡片（ZCKP）
   */
  ZCKP: {
    name: "固定资产卡片",
    type: "固定资产卡片",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@固定资产卡片", true),
      textElement("assetCode", "资产编码", "assetCode", "@资产编码"),
      textElement("assetName", "资产名称", "assetName", "@资产名称"),
      textElement("categoryName", "资产类别", "categoryName", "@资产类别"),
      textElement("assetModel", "规格型号", "assetModel", "@规格型号"),
      textElement("originalValue", "原值", "originalValue", "@原值"),
      textElement("monthlyDepreciation", "月折旧额", "monthlyDepreciation", "@月折旧额"),
      textElement("accumulatedDepreciation", "累计折旧", "accumulatedDepreciation", "@累计折旧"),
      textElement("netValue", "净值", "netValue", "@净值"),
      textElement("startDate", "启用日期", "startDate", "@启用日期"),
      textElement("status", "状态", "status", "@状态"),
      textElement("location", "存放地点", "location", "@存放地点"),
      textElement("responsiblePerson", "保管人", "responsiblePerson", "@保管人"),
      textElement("number", "资产编号", "number", "@资产编号"),
      textElement("remark", "备注", "remark", "@备注")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "资产类别", field: "categoryName" },
      { title: "规格型号", field: "assetModel" },
      { title: "原值", field: "originalValue" },
      { title: "累计折旧", field: "accumulatedDepreciation" },
      { title: "净值", field: "netValue" },
      { title: "状态", field: "status" }
    ]
  },

  /**
   * 资产标签（ZCBQ）
   */
  ZCBQ: {
    name: "资产标签",
    type: "资产标签",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@资产标签", true),
      textElement("assetCode", "资产编码", "assetCode", "@资产编码"),
      textElement("assetName", "资产名称", "assetName", "@资产名称"),
      textElement("categoryName", "资产类别", "categoryName", "@资产类别"),
      textElement("assetModel", "规格型号", "assetModel", "@规格型号"),
      textElement("location", "存放地点", "location", "@存放地点"),
      textElement("responsiblePerson", "保管人", "responsiblePerson", "@保管人"),
      textElement("number", "资产编号", "number", "@资产编号")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "资产类别", field: "categoryName" }
    ]
  },

  /**
   * 资产转固单（ZGD）
   */
  ZGD: {
    name: "资产转固单",
    type: "资产转固",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@资产转固单", true),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("billDate", "单据日期", "billDate", "@单据日期"),
      textElement("sourceType", "来源类型", "sourceType", "@来源类型"),
      textElement("organName", "供应商", "organName", "@供应商"),
      textElement("sourceBillNo", "来源单号", "sourceBillNo", "@来源单号"),
      textElement("totalAmount", "合计金额", "totalAmount", "@合计金额"),
      textElement("taxMoney", "税额", "taxMoney", "@税额"),
      textElement("discountMoney", "折扣金额", "discountMoney", "@折扣金额"),
      textElement("billDetailAllPrice", "合计", "billDetailAllPrice", "@合计"),
      textElement("billDetailCnMoney", "合计大写", "billDetailCnMoney", "@合计-大写"),
      textElement("remark", "备注", "remark", "@备注")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "物资名称", field: "materialName" },
      { title: "规格型号", field: "materialModel" },
      { title: "类别", field: "categoryName" },
      { title: "数量", field: "qty" },
      { title: "单价", field: "price" },
      { title: "金额", field: "amount" },
      { title: "备注", field: "remark" }
    ]
  },

  /**
   * 资产变动单（ZCBD）
   */
  ZCBD: {
    name: "资产变动单",
    type: "资产变动",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@资产变动单", true),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("billDate", "单据日期", "billDate", "@单据日期"),
      textElement("assetCode", "资产编码", "assetCode", "@资产编码"),
      textElement("assetName", "资产名称", "assetName", "@资产名称"),
      textElement("changeType", "变动类型", "changeType", "@变动类型"),
      textElement("beforeValue", "变动前值", "beforeValue", "@变动前值"),
      textElement("afterValue", "变动后值", "afterValue", "@变动后值"),
      textElement("changeAmount", "变动金额", "changeAmount", "@变动金额"),
      textElement("oldDepartmentName", "调出部门", "oldDepartmentName", "@调出部门"),
      textElement("newDepartmentName", "调入部门", "newDepartmentName", "@调入部门"),
      textElement("oldStatus", "原状态", "oldStatus", "@原状态"),
      textElement("newStatus", "新状态", "newStatus", "@新状态"),
      textElement("remark", "备注", "remark", "@备注")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "变动类型", field: "changeType" },
      { title: "变动金额", field: "changeAmount" }
    ]
  },

  /**
   * 资产维修单（ZCWX）
   */
  ZCWX: {
    name: "资产维修单",
    type: "资产维修",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@资产维修单", true),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("billDate", "单据日期", "billDate", "@单据日期"),
      textElement("assetCode", "资产编码", "assetCode", "@资产编码"),
      textElement("assetName", "资产名称", "assetName", "@资产名称"),
      textElement("repairType", "维修类型", "repairType", "@维修类型"),
      textElement("repairContent", "维修内容", "repairContent", "@维修内容"),
      textElement("creditCode", "贷方科目", "creditCode", "@贷方科目"),
      textElement("amount", "维修金额", "amount", "@维修金额"),
      textElement("totalAmount", "合计金额", "totalAmount", "@合计金额"),
      textElement("remark", "备注", "remark", "@备注")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "维修类型", field: "repairType" },
      { title: "金额", field: "amount" }
    ]
  },

  /**
   * 资产调拨单（ZCDB）
   */
  ZCDB: {
    name: "资产调拨单",
    type: "资产调拨",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@资产调拨单", true),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("billDate", "单据日期", "billDate", "@单据日期"),
      textElement("assetCode", "资产编码", "assetCode", "@资产编码"),
      textElement("assetName", "资产名称", "assetName", "@资产名称"),
      textElement("fromDepartmentName", "调出部门", "fromDepartmentName", "@调出部门"),
      textElement("toDepartmentName", "调入部门", "toDepartmentName", "@调入部门"),
      textElement("fromDepotName", "调出仓库", "fromDepotName", "@调出仓库"),
      textElement("toDepotName", "调入仓库", "toDepotName", "@调入仓库"),
      textElement("remark", "备注", "remark", "@备注")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "数量", field: "qty" },
      { title: "金额", field: "amount" }
    ]
  },

  /**
   * 折旧计提单（ZCJT）
   */
  ZCJT: {
    name: "折旧计提单",
    type: "折旧计提",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@折旧计提单", true),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("billDate", "单据日期", "billDate", "@单据日期"),
      textElement("depYear", "折旧年度", "depYear", "@折旧年度"),
      textElement("depMonth", "折旧月份", "depMonth", "@折旧月份"),
      textElement("assetCount", "资产数量", "assetCount", "@资产数量"),
      textElement("totalAmount", "合计折旧", "totalAmount", "@合计折旧"),
      textElement("remark", "备注", "remark", "@备注")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "折旧方法", field: "method" },
      { title: "折旧期间", field: "depreciationPeriod" },
      { title: "本期折旧", field: "depreciationAmount" },
      { title: "累计折旧", field: "accumulatedDepreciation" },
      { title: "期末净值", field: "netValue" }
    ]
  },

  /**
   * 资产处置报废单（ZCCZ）
   */
  ZCCZ: {
    name: "资产处置报废单",
    type: "资产处置报废",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@资产处置报废单", true),
      textElement("number", "单据编号", "number", "@单据编号"),
      textElement("billDate", "单据日期", "billDate", "@单据日期"),
      textElement("assetCode", "资产编码", "assetCode", "@资产编码"),
      textElement("assetName", "资产名称", "assetName", "@资产名称"),
      textElement("disposalType", "类型", "disposalType", "@类型"),
      textElement("originalValue", "原值", "originalValue", "@原值"),
      textElement("accumDep", "累计折旧", "accumDep", "@累计折旧"),
      textElement("netValue", "净值", "netValue", "@净值"),
      textElement("income", "清理收入", "income", "@清理收入"),
      textElement("expense", "清理费用", "expense", "@清理费用"),
      textElement("gainLoss", "净损益", "gainLoss", "@净损益"),
      textElement("remark", "备注", "remark", "@备注")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "类型", field: "disposalType" },
      { title: "净损益", field: "gainLoss" }
    ]
  },

  // ========== 固定资产报表 ==========

  /**
   * 折旧明细表（ASSET_DEP_DETAIL）
   */
  ASSET_DEP_DETAIL: {
    name: "折旧明细表",
    type: "折旧明细表",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@折旧明细表", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "期间范围", "dateRange", "@期间范围"),
      textElement("categoryName", "资产类别", "categoryName", "@资产类别"),
      textElement("departmentName", "部门", "departmentName", "@部门"),
      textElement("assetCode", "资产编码", "assetCode", "@资产编码"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalDepreciation", "合计本期折旧", "totalDepreciation", "@合计本期折旧")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "期间", field: "period" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "类别", field: "categoryName" },
      { title: "部门", field: "departmentName" },
      { title: "折旧方法", field: "method" },
      { title: "本期折旧额", field: "depreciationAmount" },
      { title: "累计折旧", field: "accumulatedDepreciation" },
      { title: "期末净值", field: "netValue" }
    ]
  },

  /**
   * 资产台账（ASSET_LEDGER）
   */
  ASSET_LEDGER: {
    name: "资产台账",
    type: "资产台账",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@资产台账", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "期间范围", "dateRange", "@期间范围"),
      textElement("categoryName", "资产类别", "categoryName", "@资产类别"),
      textElement("departmentName", "部门", "departmentName", "@部门"),
      textElement("status", "状态", "status", "@状态"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalOriginalValue", "合计原值", "totalOriginalValue", "@合计原值")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "规格型号", field: "assetModel" },
      { title: "类别", field: "categoryName" },
      { title: "部门", field: "departmentName" },
      { title: "原值", field: "originalValue" },
      { title: "期初累计折旧", field: "initialAccumDep" },
      { title: "本期计提", field: "periodDepreciation" },
      { title: "累计折旧", field: "accumulatedDepreciation" },
      { title: "减值准备", field: "impairmentAmount" },
      { title: "期末净值", field: "netValue" },
      { title: "状态", field: "status" }
    ]
  },

  /**
   * 变动汇总表（ASSET_CHANGE_SUMMARY）
   */
  ASSET_CHANGE_SUMMARY: {
    name: "变动汇总表",
    type: "变动汇总表",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@变动汇总表", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "日期范围", "dateRange", "@日期范围"),
      textElement("changeType", "变动类型", "changeType", "@变动类型"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalChangeAmount", "合计涉及金额", "totalChangeAmount", "@合计涉及金额")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "单据号", field: "billNo" },
      { title: "单据日期", field: "billTimeStr" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "变动类型", field: "changeType" },
      { title: "变动前值", field: "beforeValue" },
      { title: "变动后值", field: "afterValue" },
      { title: "涉及金额", field: "changeAmount" },
      { title: "操作人", field: "operator" }
    ]
  },

  /**
   * 闲置资产清单（ASSET_IDLE_LIST）
   */
  ASSET_IDLE_LIST: {
    name: "闲置资产清单",
    type: "闲置资产清单",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@闲置资产清单", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("categoryName", "资产类别", "categoryName", "@资产类别"),
      textElement("departmentName", "部门", "departmentName", "@部门"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "类别", field: "categoryName" },
      { title: "部门", field: "departmentName" },
      { title: "原值", field: "originalValue" },
      { title: "累计折旧", field: "accumulatedDepreciation" },
      { title: "净值", field: "netValue" },
      { title: "闲置月数", field: "idleMonths" },
      { title: "最近状态变更", field: "lastStatusChangeDate" }
    ]
  },

  /**
   * 业财对账表（ASSET_BIZFIN_CHECK）
   */
  ASSET_BIZFIN_CHECK: {
    name: "业财对账表",
    type: "业财对账表",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@业财对账表", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("period", "对账期间", "period", "@对账期间"),
      textElement("creatorName", "制单人", "creatorName", "@制单人")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "科目编码", field: "subjectCode" },
      { title: "科目名称", field: "subjectName" },
      { title: "资产模块余额", field: "assetValue" },
      { title: "总账余额", field: "glValue" },
      { title: "差异", field: "diff" }
    ]
  },

  /**
   * 税会差异调节表（ASSET_TAX_ADJUST）
   */
  ASSET_TAX_ADJUST: {
    name: "税会差异调节表",
    type: "税会差异调节表",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@税会差异调节表", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("period", "期间", "period", "@期间"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalAcctCurDep", "本期会计折旧合计", "totalAcctCurDep", "@本期会计折旧合计"),
      textElement("totalTaxCurDep", "本期税务折旧合计", "totalTaxCurDep", "@本期税务折旧合计")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "资产编码", field: "assetCode" },
      { title: "资产名称", field: "assetName" },
      { title: "原值", field: "originalValue" },
      { title: "本期会计折旧", field: "acctCurDep" },
      { title: "本期税务折旧", field: "taxCurDep" },
      { title: "本期差异", field: "curDiff" },
      { title: "累计会计折旧", field: "acctCumDep" },
      { title: "累计税务折旧", field: "taxCumDep" },
      { title: "累计差异", field: "cumDiff" }
    ]
  },

  /**
   * 供应商往来对账（ASSET_SUPPLIER_STATEMENT）
   */
  ASSET_SUPPLIER_STATEMENT: {
    name: "供应商往来对账",
    type: "供应商往来对账",
    headerGroup: [
      textElement("reportTitle", "报表标题", "reportTitle", "@供应商往来对账", true),
      textElement("reportDate", "打印日期", "reportDate", "@打印日期"),
      textElement("dateRange", "对账日期", "dateRange", "@对账日期"),
      textElement("supplier", "供应商", "supplier", "@供应商"),
      textElement("creatorName", "制单人", "creatorName", "@制单人"),
      textElement("totalEndNeed", "合计期末欠款", "totalEndNeed", "@合计期末欠款")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "供应商", field: "supplier" },
      { title: "联系人", field: "contacts" },
      { title: "电话", field: "telephone" },
      { title: "期初欠款", field: "beginNeed" },
      { title: "本期商品欠款", field: "goodsDebt" },
      { title: "本期资产转固欠款", field: "assetDebt" },
      { title: "本期资产核销", field: "assetPay" },
      { title: "本期商品付款", field: "pay" },
      { title: "期末欠款", field: "endNeed" }
    ]
  },

  /**
   * 送货单（SHD，收单中心打印，order-receive-plugin）
   * 数据源为 XSCK 出库单 + 出库二维码（qrContent：JSHOR|XSCK单号|防伪token），
   * 独立 billType 与主框架销售出库（XSCK）打印隔离。
   */
  SHD: {
    name: "送货单",
    type: "送货单",
    headerGroup: [
      textElement("billTitle", "单据抬头", "billTitle", "@送货单", true),
      textElement("organName", "客户", "organName", "@客户"),
      textElement("contacts", "客户联系人", "contacts", "@客户联系人"),
      textElement("telephone", "客户手机", "telephone", "@客户手机"),
      textElement("address", "客户地址", "address", "@客户地址"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("number", "送货单号", "number", "@送货单号"),
      textElement("linkNumber", "关联订单", "linkNumber", "@关联订单"),
      textElement("remark", "备注", "remark", "@备注"),
      { tid: "qrContent", title: "收货二维码", type: "qrcode", options: { field: "qrContent", testData: "@JSHOR|出库单号|防伪token" } },
      textElement("scanTip", "扫码提示", "scanTip", "@客户扫码确认收货")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "条码", field: "barCode" },
      { title: "名称", field: "name" },
      { title: "规格", field: "standard" },
      { title: "型号", field: "model" },
      { title: "单位", field: "unit" },
      { title: "数量", field: "operNumber" },
      { title: "单价", field: "unitPrice" },
      { title: "金额", field: "allPrice" }
    ]
  },

  /**
   * 餐饮预结单（YJD，dining-plugin）
   * 字段与后端 OrderService.printData 输出对齐。
   */
  YJD: {
    name: "餐饮预结单",
    type: "餐饮预结单",
    headerGroup: [
      textElement("title", "单据标题", "title", "@餐饮预结单", true),
      textElement("storeName", "门店名称", "storeName", "@门店名称"),
      textElement("tableName", "桌台名称", "tableName", "@桌台名称"),
      textElement("orderNo", "订单号", "orderNo", "@订单号"),
      textElement("orderType", "订单类型", "orderType", "@订单类型"),
      textElement("isOut", "堂食/外带", "isOut", "@堂食/外带"),
      textElement("people", "就餐人", "people", "@就餐人"),
      textElement("personCount", "人数", "personCount", "@人数"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("totalAmount", "合计", "totalAmount", "@合计"),
      textElement("serviceAmount", "服务费", "serviceAmount", "@服务费"),
      textElement("tablewareAmount", "餐位费", "tablewareAmount", "@餐位费"),
      textElement("discountAmount", "会员优惠", "discountAmount", "@会员优惠"),
      textElement("eraseAmount", "抹零", "eraseAmount", "@抹零"),
      textElement("depositAmount", "订金", "depositAmount", "@订金"),
      textElement("grossPayable", "应付", "grossPayable", "@应付"),
      textElement("payAmount", "实付", "payAmount", "@实付"),
      textElement("payType", "支付方式", "payType", "@支付方式"),
      textElement("settleTime", "结算时间", "settleTime", "@结算时间")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "品名", field: "name" },
      { title: "数量", field: "quantity" },
      { title: "单价", field: "unitPrice" },
      { title: "小计", field: "allPrice" },
      { title: "规格做法", field: "options" },
      { title: "备注", field: "remark" }
    ]
  },

  /**
   * 餐饮结账单（JZD，dining-plugin）
   * 字段与后端 OrderService.printData 输出对齐，与 YJD 同构。
   */
  JZD: {
    name: "餐饮结账单",
    type: "餐饮结账单",
    headerGroup: [
      textElement("title", "单据标题", "title", "@餐饮结账单", true),
      textElement("storeName", "门店名称", "storeName", "@门店名称"),
      textElement("tableName", "桌台名称", "tableName", "@桌台名称"),
      textElement("orderNo", "订单号", "orderNo", "@订单号"),
      textElement("orderType", "订单类型", "orderType", "@订单类型"),
      textElement("isOut", "堂食/外带", "isOut", "@堂食/外带"),
      textElement("people", "就餐人", "people", "@就餐人"),
      textElement("personCount", "人数", "personCount", "@人数"),
      textElement("remark", "备注", "remark", "@备注"),
      textElement("operTimeStr", "单据日期", "operTimeStr", "@单据日期"),
      textElement("totalAmount", "合计", "totalAmount", "@合计"),
      textElement("serviceAmount", "服务费", "serviceAmount", "@服务费"),
      textElement("tablewareAmount", "餐位费", "tablewareAmount", "@餐位费"),
      textElement("discountAmount", "会员优惠", "discountAmount", "@会员优惠"),
      textElement("eraseAmount", "抹零", "eraseAmount", "@抹零"),
      textElement("depositAmount", "订金", "depositAmount", "@订金"),
      textElement("grossPayable", "应付", "grossPayable", "@应付"),
      textElement("payAmount", "实付", "payAmount", "@实付"),
      textElement("payType", "支付方式", "payType", "@支付方式"),
      textElement("settleTime", "结算时间", "settleTime", "@结算时间")
    ],
    tableColumns: [
      { title: "序号", field: "rowIndex" },
      { title: "品名", field: "name" },
      { title: "数量", field: "quantity" },
      { title: "单价", field: "unitPrice" },
      { title: "小计", field: "allPrice" },
      { title: "规格做法", field: "options" },
      { title: "备注", field: "remark" }
    ]
  }

}

// ========== Provider 创建 ==========

/**
 * 创建 Provider
 * @param {string} billKey - 单据类型键名 (如 "LSCK", "DBCK")
 * @returns {Object} Provider 对象 { addElementTypes: function }
 */
function createProvider(billKey) {
  return {
    addElementTypes: function (hiprintInstance) {
      // 注意：hiprintInstance 是由 hiprint.init() 传入的内部实例
      // 它有 addPrintElementTypes 和 removePrintElementTypes 方法
      // 但 PrintElementTypeGroup 需要从 window.hiprint 获取

      // 获取 window.hiprint
      const hiprint = window.hiprint || getHiprint()
      if (!hiprint) {
        console.error('[hiprint-register] hiprint 未初始化')
        return
      }

      // 验证必要的方法存在
      if (!hiprintInstance || typeof hiprintInstance.removePrintElementTypes !== 'function') {
        console.error('[hiprint-register] hiprintInstance 无效')
        return
      }

      // 更新扩展字段标题
      updateExtendTitles()

      // 获取配置
      const config = BILL_CONFIGS[billKey]
      if (!config) {
        console.error('[hiprint-register] 未找到单据类型配置:', billKey)
        return
      }

      // 移除旧的元素类型
      hiprintInstance.removePrintElementTypes(billKey)

      // 创建表格元素（替换扩展字段标题）
      const tableColumnsWithExt = config.tableColumns.map(col => {
        let title = col.title
        if (title === "扩展1") title = extendTitles.extend1
        if (title === "扩展2") title = extendTitles.extend2
        if (title === "扩展3") title = extendTitles.extend3
        return { ...col, title }
      })
      const tableElement = createTableElement(billKey, tableColumnsWithExt)

      // 使用 window.hiprint.PrintElementTypeGroup 创建元素组
      // 然后用 hiprintInstance.addPrintElementTypes 添加到系统
      hiprintInstance.addPrintElementTypes(billKey, [
        new hiprint.PrintElementTypeGroup("单据主体", config.headerGroup),
        new hiprint.PrintElementTypeGroup("单据明细", [tableElement]),
        new hiprint.PrintElementTypeGroup("公司信息", COMPANY_INFO),
        new hiprint.PrintElementTypeGroup("其他信息", OTHER_INFO)
      ])
    }
  }
}

// ========== Provider 列表 ==========

/**
 * 获取所有单据类型的 Provider 列表
 * @returns {Array} Provider 数组 [{ name, value, type, f }]
 */
export function getProvidersArr() {
  return [
    { name: "零售出库单", value: "LSCK", type: "零售出库", f: createProvider("LSCK") },
    { name: "零售退货单", value: "LSTH", type: "零售退货入库", f: createProvider("LSTH") },
    { name: "请购单", value: "QGD", type: "请购单", f: createProvider("QGD") },
    { name: "采购订单", value: "CGDD", type: "采购订单", f: createProvider("CGDD") },
    { name: "采购入库", value: "CGRK", type: "采购入库", f: createProvider("CGRK") },
    { name: "采购退货", value: "CGTH", type: "采购退货出库", f: createProvider("CGTH") },
    { name: "销售订单", value: "XSDD", type: "销售订单", f: createProvider("XSDD") },
    { name: "销售出库", value: "XSCK", type: "销售出库", f: createProvider("XSCK") },
    { name: "销售退货", value: "XSTH", type: "销售退货入库", f: createProvider("XSTH") },
    { name: "其他入库", value: "QTRK", type: "其他入库", f: createProvider("QTRK") },
    { name: "其他出库", value: "QTCK", type: "其他出库", f: createProvider("QTCK") },
    { name: "调拨出库", value: "DBCK", type: "调拨出库", f: createProvider("DBCK") },
    { name: "组装单", value: "ZZD", type: "组装单", f: createProvider("ZZD") },
    { name: "拆卸单", value: "CXD", type: "拆卸单", f: createProvider("CXD") },
    { name: "销售排行", value: "SALES_RANKING", type: "销售排行", f: createProvider("SALES_RANKING") },
    { name: "收支项明细", value: "ITEM_DETAIL", type: "收支项明细", f: createProvider("ITEM_DETAIL") },
    { name: "销售占比", value: "SALE_RATIO", type: "销售占比", f: createProvider("SALE_RATIO") },
    { name: "收付款明细", value: "MONEY_DETAIL", type: "收付款明细", f: createProvider("MONEY_DETAIL") },
    { name: "利润报表", value: "SALE_PROFIT", type: "利润报表", f: createProvider("SALE_PROFIT") },
    { name: "利润报表", value: "PROFIT_REPORT", type: "利润报表", f: createProvider("PROFIT_REPORT") },
    { name: "盘点录入", value: "PDLR", type: "盘点录入", f: createProvider("PDLR") },
    { name: "盘点复盘", value: "PDFP", type: "盘点复盘", f: createProvider("PDFP") },
    { name: "资产申领", value: "NSLD", type: "资产申领", f: createProvider("NSLD") },
    { name: "资产出库", value: "NCKD", type: "资产出库", f: createProvider("NCKD") },
    { name: "负库存统计", value: "MINUS_STOCK", type: "负库存统计", f: createProvider("MINUS_STOCK") },
    { name: "多属性库存统计", value: "ATTRIBUTE_STOCK", type: "多属性库存统计", f: createProvider("ATTRIBUTE_STOCK") },
    { name: "序列号统计", value: "SERIAL_NUMBER_STATISTICS", type: "序列号统计", f: createProvider("SERIAL_NUMBER_STATISTICS") },
    { name: "固定资产卡片", value: "ZCKP", type: "固定资产卡片", f: createProvider("ZCKP") },
    { name: "资产标签", value: "ZCBQ", type: "资产标签", f: createProvider("ZCBQ") },
    { name: "资产转固单", value: "ZGD", type: "资产转固", f: createProvider("ZGD") },
    { name: "资产变动单", value: "ZCBD", type: "资产变动", f: createProvider("ZCBD") },
    { name: "资产维修单", value: "ZCWX", type: "资产维修", f: createProvider("ZCWX") },
    { name: "资产调拨单", value: "ZCDB", type: "资产调拨", f: createProvider("ZCDB") },
    { name: "折旧计提单", value: "ZCJT", type: "折旧计提", f: createProvider("ZCJT") },
    { name: "资产处置报废单", value: "ZCCZ", type: "资产处置报废", f: createProvider("ZCCZ") },
    { name: "折旧明细表", value: "ASSET_DEP_DETAIL", type: "折旧明细表", f: createProvider("ASSET_DEP_DETAIL") },
    { name: "资产台账", value: "ASSET_LEDGER", type: "资产台账", f: createProvider("ASSET_LEDGER") },
    { name: "变动汇总表", value: "ASSET_CHANGE_SUMMARY", type: "变动汇总表", f: createProvider("ASSET_CHANGE_SUMMARY") },
    { name: "闲置资产清单", value: "ASSET_IDLE_LIST", type: "闲置资产清单", f: createProvider("ASSET_IDLE_LIST") },
    { name: "业财对账表", value: "ASSET_BIZFIN_CHECK", type: "业财对账表", f: createProvider("ASSET_BIZFIN_CHECK") },
    { name: "税会差异调节表", value: "ASSET_TAX_ADJUST", type: "税会差异调节表", f: createProvider("ASSET_TAX_ADJUST") },
    { name: "供应商往来对账", value: "ASSET_SUPPLIER_STATEMENT", type: "供应商往来对账", f: createProvider("ASSET_SUPPLIER_STATEMENT") },
    { name: "送货单", value: "SHD", type: "送货单", f: createProvider("SHD") },
    { name: "餐饮预结单", value: "YJD", type: "餐饮预结单", f: createProvider("YJD") },
    { name: "餐饮结账单", value: "JZD", type: "餐饮结账单", f: createProvider("JZD") }
  ]
}

// ========== 注册函数 ==========

/**
 * 注册单个单据类型到 hiprint
 * @param {string} billKey - 单据类型键名
 * @returns {boolean} 是否成功
 */
export function registerBillType(billKey) {
  const hiprint = getHiprint()
  if (!hiprint) {
    console.error('[hiprint-register] hiprint 实例未初始化')
    return false
  }

  const config = BILL_CONFIGS[billKey]
  if (!config) {
    console.error('[hiprint-register] 未找到单据类型配置:', billKey)
    return false
  }

  // 获取 provider 并执行
  const provider = createProvider(billKey)
  provider.addElementTypes(hiprint)

  return true
}

/**
 * 注册所有单据类型到 hiprint
 */
export function registerAllBillTypes() {
  const hiprint = getHiprint()
  if (!hiprint) {
    console.error('[hiprint-register] hiprint 实例未初始化')
    return false
  }

  const providers = getProvidersArr()
  providers.forEach(provider => {
    try {
      provider.f.addElementTypes(hiprint)
    } catch (e) {
      console.error('[hiprint-register] 注册失败:', provider.value, e)
    }
  })

  return true
}

/**
 * 根据 type（中文全名）查找 Provider
 * @param {string} type - 单据类型中文全名（如 "零售出库"）
 * @returns {Object|null} Provider 对象
 */
export function findProviderByType(type) {
  const providers = getProvidersArr()
  return providers.find(p => p.type === type) || null
}

/**
 * 根据 value（英文缩写）查找配置
 * @param {string} value - 单据类型缩写（如 "LSCK"）
 * @returns {Object|null} 配置对象
 */
export function findConfigByValue(value) {
  return BILL_CONFIGS[value] || null
}

/**
 * 获取所有配置的 key 列表
 * @returns {Array} 键名数组
 */
export function getAllBillKeys() {
  return Object.keys(BILL_CONFIGS)
}

// ========== 默认模板 ==========

/**
 * 默认模板数据（用于没有后端模板时的降级）
 * 实际使用时应从后端 API 或 default_templates.js 获取
 */
export const DEFAULT_TEMPLATES = {}

/**
 * 获取默认模板
 * @param {string} billType - 单据类型
 * @returns {Object|null} 默认模板 JSON 对象
 */
export function getDefaultTemplate(billType) {
  if (DEFAULT_TEMPLATES[billType]) {
    return DEFAULT_TEMPLATES[billType]
  }
  return null
}
